import {
	keepPreviousData,
	useInfiniteQuery,
	useIsMutating,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query"
import {
	ErrorResponse,
	InfiniteData,
	InfinitivePage,
	SuccessCursorResponse,
	SuccessPageResponse,
	SuccessResponse,
	useHttpClient,
} from "api/api"
import { SortValues } from "interface/sort"
import {
	AdminVisitCreatePayload,
	AdminVisitUpdatePayload,
	Visit,
	VisitPatient,
} from "interface/visit"
import { useInvalidateOnRealtimeEvent } from "modules/realtime/useInvalidateOnRealtimeEvent"
import { useMemo } from "react"
import { useParams } from "react-router"
import { asDate } from "utils/date"
import { queryKeys as patientsQueryKeys } from "./patients"

export const queryKeys = {
	visits: "visits",
	visit: "visit",
	visitUpdate: "visit-update",
	visitUpdatePhoto: "visit-update-photo",
	visitsOwn: "visits-own",
	visitsConflict: "visits-conflict",
	visitRegisterDevice: "visit-register-device",
	visitUnregisterDevice: "visit-unregister-device",
	visitSendLoginLink: "visit-send-login-link",
	visitOwniCalUrl: "visit-own-ical-url",
	visitAddComment: "visit-add-comment",
	visitsAdminList: "visits-admin-list",
	visitsAdminPatients: "visits-admin-patients",
	visitAdminCreate: "visit-admin-create",
	visitAdminUpdate: "visit-admin-update",
}

export const useAdminVisits = ({
	status,
	type,
	sort,
	query,
	page = "1",
	fieldSupportTeamId,
}: {
	status?: Array<Visit["status"]>
	type?: Array<Visit["visitType"]>
	sort: string[]
	query?: string
	page?: string
	fieldSupportTeamId?: string[]
}) => {
	const { httpClient } = useHttpClient()
	const params = useMemo(
		() => ({
			limit: 20,
			page,
			status,
			type,
			sort,
			query: query?.trim() || undefined,
			fieldSupportTeamId,
		}),
		[fieldSupportTeamId, page, query, sort, status, type],
	)

	return useQuery({
		queryKey: [queryKeys.visitsAdminList, params],
		queryFn: async ({ signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessPageResponse<Visit>>(
				"/api/v1/admin/visits/list",
				{
					signal,
					params,
				},
			)

			return data
		},
		placeholderData: keepPreviousData,
	})
}

export const useAdminVisitPatients = () => {
	const { httpClient } = useHttpClient()

	return useQuery<VisitPatient[], ErrorResponse>({
		queryKey: [queryKeys.visitsAdminPatients],
		queryFn: async ({ signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<VisitPatient[]>>(
				"/api/v1/admin/visits/patients",
				{
					signal,
				},
			)

			return data
		},
	})
}

export const useAdminVisitCreate = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<void, ErrorResponse, AdminVisitCreatePayload>({
		mutationKey: [queryKeys.visitAdminCreate],
		mutationFn: (payload) => httpClient.post("/api/v1/admin/visits", payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [queryKeys.visitsAdminList] })
			queryClient.invalidateQueries({
				queryKey: [queryKeys.visitsAdminPatients],
				refetchType: "none",
			})
		},
	})
}

export const useAdminVisitUpdate = (visitId: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<void, ErrorResponse, AdminVisitUpdatePayload>({
		mutationKey: [queryKeys.visitAdminUpdate, visitId],
		mutationFn: (payload) =>
			httpClient.patch(`/api/v1/admin/visits/${visitId}`, payload, {
				headers: { "Content-Type": "multipart/form-data" },
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [queryKeys.visitsAdminList] })
		},
	})
}

export const useVisits = ({
	status,
	sort,
	query,
	onlyUnreadMessages,
}: {
	status?: Array<Visit["status"]>
	sort: SortValues<Visit>
	query?: string
	onlyUnreadMessages?: boolean
}) => {
	const { httpClient } = useHttpClient()
	const { scopeId: fieldSupportTeamId } = useParams()
	const params = useMemo(
		() => ({
			limit: 20,
			status,
			sort,
			query: query?.trim() || undefined,
			fieldSupportTeamId,
			onlyUnreadMessages,
		}),
		[fieldSupportTeamId, onlyUnreadMessages, query, sort, status],
	)
	const queryKey = useMemo(() => [queryKeys.visits, params], [params])

	useInvalidateOnRealtimeEvent("unread_count_update", {
		queryKey,
		queryPredicate(dataQuery, data) {
			const hasPatient = (
				dataQuery.state.data as InfiniteData<Visit[]> | undefined
			)?.pages
				?.flatMap(({ data: items }) => items)
				?.flat()
				?.some(({ patient }) => patient?.id === data.patientId)

			return hasPatient ?? false
		},
	})

	return useInfiniteQuery<InfinitivePage<Visit>, Error, { pages: Visit[] }>({
		enabled: !!params.fieldSupportTeamId,
		queryKey: [queryKeys.visits, params],
		queryFn: async ({ pageParam, signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessCursorResponse<Visit>>("/api/v1/visits", {
				signal,
				params: {
					...params,
					cursor: pageParam,
				},
			})

			return {
				data: data.items,
				nextCursor: data.nextCursor,
			}
		},
		initialPageParam: undefined,
		placeholderData: keepPreviousData,
		select: ({ pages }) => {
			return {
				pages: pages.flatMap(({ data }) => data),
			}
		},
		getNextPageParam: (lastPage) => lastPage.nextCursor,
	})
}

export const useOwnVisits = () => {
	const { httpClient } = useHttpClient()
	const { scopeId: fieldSupportTeamId } = useParams()
	const queryKey = useMemo(
		() => [queryKeys.visitsOwn, fieldSupportTeamId],
		[fieldSupportTeamId],
	)

	useInvalidateOnRealtimeEvent("unread_count_update", {
		queryKey,
		queryPredicate(query, data) {
			const hasPatient = (query.state.data as Visit[] | undefined)?.some(
				({ patient }) => patient?.id === data.patientId,
			)
			return hasPatient ?? false
		},
	})

	return useQuery({
		queryKey,
		queryFn: async ({ signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<Visit[]>>("/api/v1/visits/me", {
				signal,
				params: { fieldSupportTeamId },
			})

			return data
		},
		placeholderData: keepPreviousData,
		select: (data) =>
			data.sort(
				(a, b) => asDate(a.visitDate!).unix() - asDate(b.visitDate!).unix(),
			),
	})
}

export const useVisitsConflict = ({
	visitId,
	...params
}: {
	visitId: string
	agentUserIds?: string[]
	start?: string
	end?: string
}) => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: [queryKeys.visitsConflict, { ...params, visitId }],
		queryFn: async ({ signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<Visit[]>>(
				`/api/v1/visits/${visitId}/conflicts`,
				{
					signal,
					params,
				},
			)

			return data
		},
		enabled:
			!!params.agentUserIds &&
			params.agentUserIds.length > 0 &&
			!!params.start &&
			!!params.end,
		placeholderData: keepPreviousData,
		select: (data) =>
			data.sort(
				(a, b) => asDate(a.visitDate!).unix() - asDate(b.visitDate!).unix(),
			),
	})
}

export const useOwniCalUrl = () => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: [queryKeys.visitOwniCalUrl],
		queryFn({ signal }) {
			return httpClient.get<SuccessResponse<{ iCalUrl: string }>>(
				"/api/v1/visits/ical/me",
				{
					signal,
				},
			)
		},
		placeholderData: keepPreviousData,
		select({ data }) {
			return data.data.iCalUrl
		},
	})
}

export const useVisit = (visitId: string) => {
	const { httpClient } = useHttpClient()
	const queryKey = useMemo(() => [queryKeys.visit, visitId], [visitId])

	useInvalidateOnRealtimeEvent("unread_count_update", {
		queryKey,
		queryPredicate(query, data) {
			return (
				(query.state.data as Visit | undefined)?.patient?.id === data.patientId
			)
		},
	})

	return useQuery({
		queryKey,
		queryFn: async ({ signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<Visit>>(
				`/api/v1/visits/${visitId}`,
				{
					signal,
				},
			)

			return data
		},
		enabled: !!visitId,
	})
}

export const useUpdateVisit = (visitId: string, isMarkComplete = false) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()
	const url = `/api/v1/visits/${visitId}${isMarkComplete ? "/mark-visit-complete" : ""}`
	type VisitPayload = Partial<Pick<Visit, "status">> &
		Pick<
			Visit,
			| "visitDate"
			| "visitDurationInMinutes"
			| "notes"
			| "patient"
			| "cancelDate"
			| "completedDate"
		> & {
			agentUserIds?: string[]
			installationPhotoFile?: File
		}

	return useMutation<Visit, ErrorResponse, VisitPayload>({
		mutationKey: [queryKeys.visitUpdate],
		mutationFn: async (visit: VisitPayload) =>
			httpClient.patch(url, visit, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.visits],
			})
			queryClient.invalidateQueries({
				queryKey: [queryKeys.visitsOwn],
			})
			queryClient.invalidateQueries({
				queryKey: [queryKeys.visit, visitId],
			})
			queryClient.invalidateQueries({
				queryKey: [patientsQueryKeys.patientDetails],
			})
		},
	})
}

export const useUpdateVisitPhotoFile = (visitId: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()
	const { scopeId } = useParams()

	return useMutation<Visit, ErrorResponse, File>({
		mutationKey: [queryKeys.visitUpdatePhoto],
		mutationFn: async (installationPhotoFile: File) =>
			httpClient.post(
				`/api/v1/visits/${visitId}/photo`,
				{ installationPhotoFile, fieldSupportTeamId: scopeId },
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				},
			),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.visit, visitId],
			})
		},
	})
}

export const useRegisterDevice = (visitId: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: [queryKeys.visitRegisterDevice, visitId],
		mutationFn: async (serialNumber: string) =>
			httpClient.post(`/api/v1/visits/${visitId}/register-device`, {
				serialNumber,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [patientsQueryKeys.patientDevice],
			})
		},
	})
}

export const useUnregisterDevice = (visitId: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: [queryKeys.visitUnregisterDevice, visitId],
		mutationFn: (deviceId: string) =>
			httpClient.post(`/api/v1/visits/${visitId}/unregister-device`, {
				deviceId,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [patientsQueryKeys.patientDevice],
			})
		},
	})
}

export const useIsMutatingVisit = () => {
	return useIsMutating({ mutationKey: [queryKeys.visitUpdate] }) > 0
}

export const useSendLoginLink = (visitId: string) => {
	const { httpClient } = useHttpClient()

	return useMutation({
		mutationKey: [queryKeys.visitUpdate],
		mutationFn: (method: "sms" | "email") =>
			httpClient.post(`/api/v1/visits/${visitId}/send-login-link`, { method }),
	})
}

export const useAddComment = (visitId: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: [queryKeys.visitAddComment, visitId],
		mutationFn: (content: string) =>
			httpClient.post(`/api/v1/visits/${visitId}/comment`, {
				content,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.visit, visitId],
			})
			queryClient.invalidateQueries({
				queryKey: [queryKeys.visitsOwn],
			})
		},
	})
}
